import { MapPin, Phone, Mail, Clock, ArrowUpRight } from "lucide-react";
import { useContent } from "@/i18n";
import { firm, fullAddress, mapsEmbedUrl, mapsLink, telHref } from "@/config/firm.config";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./SectionHeading";
import { EnquiryForm } from "./EnquiryForm";

const Row = ({ icon: Icon, label, children, testId }) => (
  <div className="grid grid-cols-[auto_1fr] gap-4 border-t border-white/10 py-6" data-testid={testId}>
    <Icon size={16} strokeWidth={1.4} className="mt-1 text-gold" aria-hidden="true" />
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">{label}</p>
      <div className="mt-2 text-base leading-relaxed text-paper/90">{children}</div>
    </div>
  </div>
);

export const Contact = () => {
  const { t } = useContent();
  return (
    <section id="contact" data-testid="contact-section" className="relative bg-navy-900 py-28 md:py-40">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <SectionHeading eyebrow={t.contact.eyebrow} title={t.contact.title} intro={t.contact.intro} />
        <div className="mt-20 grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <address className="not-italic">
              <Row icon={MapPin} label={t.contact.addressLabel} testId="contact-address">
                <p>{firm.address.line1}</p>
                <p>{firm.address.line2}, {firm.address.city}</p>
                <p className="text-paper/70">{firm.address.landmark}</p>
                <p>{firm.address.state} – {firm.address.pin}</p>
                <a href={mapsLink} target="_blank" rel="noopener noreferrer" data-testid="contact-directions-link" className="focus-ring mt-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold hover:text-gold-light">
                  {t.contact.directions} <ArrowUpRight size={12} />
                </a>
              </Row>
              <Row icon={Phone} label={t.contact.phoneLabel} testId="contact-phones">
                <ul className="space-y-1.5">
                  {firm.advocates.map((a) => (
                    <li key={a.id}>
                      <a href={telHref(a.phone)} data-testid={`contact-phone-${a.id}`} className="focus-ring hover:text-gold-light">{a.phone}</a>
                      <span className="ml-2 text-xs text-ink/50">{a.name}</span>
                    </li>
                  ))}
                </ul>
              </Row>
              <Row icon={Mail} label={t.contact.emailLabel} testId="contact-email">
                <a href={`mailto:${firm.email}`} data-testid="contact-email-link" className="focus-ring break-all hover:text-gold-light">{firm.email}</a>
              </Row>
              <Row icon={Clock} label={t.contact.hoursLabel} testId="contact-hours">
                {firm.hours.map((h) => (
                  <p key={h}>{h}</p>
                ))}
              </Row>
            </address>
            <div className="relative mt-8 border border-gold/30 p-2" data-testid="contact-map">
              <iframe
                title={t.contact.mapTitle}
                src={mapsEmbedUrl}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                className="h-64 w-full grayscale-[60%] contrast-[1.05] transition-[filter] duration-700 hover:grayscale-0 sm:h-72"
              />
              <span className="sr-only">{fullAddress}</span>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <EnquiryForm />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
